import {
  ClarificationActionResultV1Schema,
  ClarificationListV1Schema,
  ClarificationRespondRequestV1Schema,
  ClientIntakeDraftSaveRequestV1Schema,
  ClientIntakeDraftSaveResultV1Schema,
  ClientIntakeSubmitRequestV1Schema,
  ClientIntakeSubmitResultV1Schema,
  ClientIntakeViewV1Schema,
  type ClientIntakeDraftSaveRequestV1,
  type ClientIntakeDraftSaveResultV1,
  type ClarificationActionResultV1,
  type ClarificationListV1,
  type ClarificationRespondRequestV1,
  type ClientIntakeSubmitRequestV1,
  type ClientIntakeSubmitResultV1,
  type ClientIntakeViewV1,
  ClientSafeEditPatchV1Schema,
  ClientSafeHandoffViewV1Schema,
  ClientSafePatchOutcomeV1Schema,
  ClientSafeReportViewV1Schema,
  ClientSafeLaunchProofViewV1Schema,
  ClientSafeGovernanceViewV1Schema,
  ClientSafeLaunchCertificateViewV1Schema,
  ClientSafeWebsiteOwnershipViewV1Schema,
  type ClientSafeGovernanceViewV1,
  type ClientSafeEditPatchV1,
  type ClientSafeHandoffViewV1,
  type ClientSafePatchOutcomeV1,
  type ClientSafeReportViewV1,
  type ClientSafeLaunchProofViewV1,
  type ClientSafeLaunchCertificateViewV1,
  type ClientSafeWebsiteOwnershipViewV1,
  ExternalProofSalesRoomApprovalRecordV1Schema,
  ExternalProofSalesRoomClientSafeViewV1Schema,
  ExternalAfterFixProofClientSafeSummaryV1Schema,
  SanitizedProjectRecordCenterClientViewV1Schema,
  ClientJourneyPresentationV1Schema,
  type ExternalProofSalesRoomApprovalRecordV1,
  type ExternalProofSalesRoomClientSafeViewV1,
  type ExternalAfterFixProofClientSafeSummaryV1,
  type SanitizedProjectRecordCenterClientViewV1,
  type ClientJourneyPresentationV1,
} from "@enrein/shared-contracts";
import { ClientPortalShellViewV1Schema, type ClientPortalShellViewV1 } from "@enrein/shared-contracts/client-link";
import {
  ClientLinkSessionCreateResultV1Schema,
  type ClientLinkSessionCreateResultV1,
} from "@enrein/shared-contracts/client-link";
import {
  ApprovalRecordV1Schema,
  ChangeRequestV1Schema,
  ClientChangeRequestTimelineV1Schema,
  type ClientChangeRequestTimelineV1,
  PreviewViewV1Schema,
  type ApprovalRecordV1,
  type ChangeRequestV1,
  type PreviewViewV1,
} from "@enrein/shared-contracts/approval-change";
import type { ClientPortalRuntimeConfig } from "./env.js";

interface ClientPortalErrorResponse {
  readonly error?: string;
}

async function parseJsonOrThrow(res: Response): Promise<unknown> {
  const body = (await res.json()) as unknown;
  if (!res.ok) {
    const maybe = body as ClientPortalErrorResponse;
    throw new Error(maybe.error ?? `client_portal_api_error_${res.status}`);
  }
  return body;
}

function enc(value: string): string {
  return encodeURIComponent(value);
}

export interface ClientPortalApiClient {
  readonly createSession: (clientLinkId: string, token: string) => Promise<ClientLinkSessionCreateResultV1>;
  readonly getShell: (clientLinkId: string, token: string) => Promise<ClientPortalShellViewV1>;
  readonly getPreview: (clientLinkId: string, token: string, route?: string) => Promise<PreviewViewV1>;
  readonly approve: (
    clientLinkId: string,
    token: string,
    previewVersionId: string,
    approvedByName?: string,
  ) => Promise<ApprovalRecordV1>;
  readonly requestChange: (clientLinkId: string, token: string, message: string, requestedByName?: string) => Promise<ChangeRequestV1>;
  readonly listChangeRequests: (clientLinkId: string, token: string) => Promise<ClientChangeRequestTimelineV1>;
  readonly submitSafeEditPatch: (
    clientLinkId: string,
    token: string,
    patch: ClientSafeEditPatchV1,
  ) => Promise<ClientSafePatchOutcomeV1>;
  readonly getSafeEditOutcome: (clientLinkId: string, token: string, attemptId: string) => Promise<ClientSafePatchOutcomeV1>;
  readonly getCurrentClientReport: (clientLinkId: string, token: string) => Promise<ClientSafeReportViewV1>;
  readonly getCurrentClientLaunchProof: (
    clientLinkId: string,
    token: string,
  ) => Promise<ClientSafeLaunchProofViewV1>;
  readonly getCurrentClientLaunchCertificate: (
    clientLinkId: string,
    token: string,
  ) => Promise<ClientSafeLaunchCertificateViewV1>;
  readonly getClientWebsiteOwnershipRecord: (
    clientLinkId: string,
    token: string,
  ) => Promise<ClientSafeWebsiteOwnershipViewV1>;
  readonly getHandoff: (clientLinkId: string, token: string) => Promise<ClientSafeHandoffViewV1>;
  readonly downloadHandoff: (clientLinkId: string, token: string) => Promise<Blob>;
  readonly getGovernance: (clientLinkId: string, token: string) => Promise<ClientSafeGovernanceViewV1>;
  readonly getIntake: (clientLinkId: string, token: string) => Promise<ClientIntakeViewV1>;
  readonly submitIntake: (
    clientLinkId: string,
    token: string,
    payload: ClientIntakeSubmitRequestV1,
  ) => Promise<ClientIntakeSubmitResultV1>;
  readonly saveIntakeDraft: (
    clientLinkId: string,
    token: string,
    payload: ClientIntakeDraftSaveRequestV1,
    options?: { readonly keepalive?: boolean },
  ) => Promise<ClientIntakeDraftSaveResultV1 | null>;
  readonly getExternalProofSalesRoom: (
    clientLinkId: string,
    token: string,
  ) => Promise<ExternalProofSalesRoomClientSafeViewV1>;
  readonly approveExternalProofSalesRoom: (
    clientLinkId: string,
    token: string,
    salesRoomId: string,
    viewVersion: string,
    approvedByName?: string,
    note?: string,
  ) => Promise<ExternalProofSalesRoomApprovalRecordV1>;
  readonly getExternalAfterFixProof: (
    clientLinkId: string,
    token: string,
  ) => Promise<ExternalAfterFixProofClientSafeSummaryV1>;
  readonly getProjectRecordCenter: (
    clientLinkId: string,
    token: string,
  ) => Promise<SanitizedProjectRecordCenterClientViewV1>;
  readonly listClarifications: (clientLinkId: string, token: string) => Promise<ClarificationListV1>;
  readonly respondToClarification: (
    clientLinkId: string,
    token: string,
    clarificationRequestId: string,
    body: ClarificationRespondRequestV1,
  ) => Promise<ClarificationActionResultV1>;
  readonly getJourneyPresentation: (
    clientLinkId: string,
    token: string,
  ) => Promise<ClientJourneyPresentationV1>;
}

function authHeaders(token: string, extra?: HeadersInit): HeadersInit {
  return { ...extra, authorization: `Bearer ${token}` };
}

export function createClientPortalApiClient(
  config: ClientPortalRuntimeConfig,
  fetchImpl: typeof fetch = fetch,
): ClientPortalApiClient {
  return {
    async createSession(clientLinkId: string, token: string) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/session`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ schemaId: "client-link.sessionCreateRequest.v1", token }),
        },
      );
      const body = await parseJsonOrThrow(res);
      return ClientLinkSessionCreateResultV1Schema.parse(body);
    },
    async getShell(clientLinkId: string, token: string) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/shell`,
        { method: "GET", headers: authHeaders(token) },
      );
      const body = await parseJsonOrThrow(res);
      return ClientPortalShellViewV1Schema.parse(body);
    },
    async getPreview(clientLinkId: string, token: string, route?: string) {
      const qs = route ? `?route=${encodeURIComponent(route)}` : "";
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/preview${qs}`,
        { method: "GET", headers: authHeaders(token) },
      );
      const body = await parseJsonOrThrow(res);
      return PreviewViewV1Schema.parse(body);
    },
    async approve(clientLinkId: string, token: string, previewVersionId: string, approvedByName?: string) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/approval`,
        {
          method: "POST",
          headers: authHeaders(token, { "content-type": "application/json" }),
          body: JSON.stringify({
            schemaId: "approval-submission.v1",
            previewVersionId,
            approvedByName,
            consentAcknowledged: true as const,
          }),
        },
      );
      const body = await parseJsonOrThrow(res);
      return ApprovalRecordV1Schema.parse(body);
    },
    async requestChange(clientLinkId: string, token: string, message: string, requestedByName?: string) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/change-requests`,
        {
          method: "POST",
          headers: authHeaders(token, { "content-type": "application/json" }),
          body: JSON.stringify({ schemaId: "change-request.create.v1", message, requestedByName }),
        },
      );
      const body = await parseJsonOrThrow(res);
      return ChangeRequestV1Schema.parse(body);
    },
    async listChangeRequests(clientLinkId: string, token: string) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/change-requests`,
        { method: "GET", headers: authHeaders(token) },
      );
      const body = await parseJsonOrThrow(res);
      return ClientChangeRequestTimelineV1Schema.parse(body);
    },
    async submitSafeEditPatch(clientLinkId: string, token: string, patch: ClientSafeEditPatchV1) {
      const validated = ClientSafeEditPatchV1Schema.parse(patch);
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/safe-edit-patches`,
        {
          method: "POST",
          headers: authHeaders(token, { "content-type": "application/json" }),
          body: JSON.stringify(validated),
        },
      );
      const body = await parseJsonOrThrow(res);
      return ClientSafePatchOutcomeV1Schema.parse(body);
    },
    async getSafeEditOutcome(clientLinkId: string, token: string, attemptId: string) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/safe-edit-patches/${enc(attemptId)}`,
        { method: "GET", headers: authHeaders(token) },
      );
      const body = await parseJsonOrThrow(res);
      return ClientSafePatchOutcomeV1Schema.parse(body);
    },
    async getCurrentClientReport(clientLinkId: string, token: string) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/reports/current`,
        { method: "GET", headers: authHeaders(token) },
      );
      const body = await parseJsonOrThrow(res);
      return ClientSafeReportViewV1Schema.parse(body);
    },
    async getCurrentClientLaunchProof(clientLinkId: string, token: string) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/launch-proof/current`,
        { method: "GET", headers: authHeaders(token) },
      );
      const body = await parseJsonOrThrow(res);
      return ClientSafeLaunchProofViewV1Schema.parse(body);
    },
    async getCurrentClientLaunchCertificate(clientLinkId: string, token: string) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/launch-certificate/current`,
        { method: "GET", headers: authHeaders(token) },
      );
      const body = await parseJsonOrThrow(res);
      return ClientSafeLaunchCertificateViewV1Schema.parse(body);
    },
    async getClientWebsiteOwnershipRecord(clientLinkId: string, token: string) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/website-ownership-record`,
        { method: "GET", headers: authHeaders(token) },
      );
      const body = await parseJsonOrThrow(res);
      return ClientSafeWebsiteOwnershipViewV1Schema.parse(body);
    },
    async getHandoff(clientLinkId: string, token: string) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/handoff`,
        { method: "GET", headers: authHeaders(token) },
      );
      const body = await parseJsonOrThrow(res);
      return ClientSafeHandoffViewV1Schema.parse(body);
    },
    async downloadHandoff(clientLinkId: string, token: string) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/handoff/download`,
        { method: "GET", headers: authHeaders(token) },
      );
      if (!res.ok) {
        const maybe = (await res.json()) as ClientPortalErrorResponse;
        throw new Error(maybe.error ?? `client_portal_api_error_${res.status}`);
      }
      return res.blob();
    },
    async getGovernance(clientLinkId: string, token: string) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/governance`,
        { method: "GET", headers: authHeaders(token) },
      );
      const body = await parseJsonOrThrow(res);
      return ClientSafeGovernanceViewV1Schema.parse(body);
    },
    async getIntake(clientLinkId: string, token: string) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/intake`,
        { method: "GET", headers: authHeaders(token) },
      );
      const body = await parseJsonOrThrow(res);
      return ClientIntakeViewV1Schema.parse(body);
    },
    async submitIntake(clientLinkId: string, token: string, payload: ClientIntakeSubmitRequestV1) {
      const validated = ClientIntakeSubmitRequestV1Schema.parse(payload);
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/intake`,
        {
          method: "POST",
          headers: authHeaders(token, { "content-type": "application/json" }),
          body: JSON.stringify(validated),
        },
      );
      const body = await parseJsonOrThrow(res);
      return ClientIntakeSubmitResultV1Schema.parse(body);
    },
    async saveIntakeDraft(
      clientLinkId: string,
      token: string,
      payload: ClientIntakeDraftSaveRequestV1,
      options?: { readonly keepalive?: boolean },
    ) {
      const validated = ClientIntakeDraftSaveRequestV1Schema.parse(payload);
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/intake/draft`,
        {
          method: "PUT",
          headers: authHeaders(token, { "content-type": "application/json" }),
          body: JSON.stringify(validated),
          keepalive: options?.keepalive === true,
        },
      );
      if (options?.keepalive === true) {
        // pagehide/beforeunload: best-effort persist; body may be unread during unload.
        if (!res.ok) return null;
        try {
          const body = await parseJsonOrThrow(res);
          return ClientIntakeDraftSaveResultV1Schema.parse(body);
        } catch {
          return null;
        }
      }
      const body = await parseJsonOrThrow(res);
      return ClientIntakeDraftSaveResultV1Schema.parse(body);
    },
    async getExternalProofSalesRoom(clientLinkId: string, token: string) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/external-proof-sales-room`,
        { method: "GET", headers: authHeaders(token) },
      );
      const body = await parseJsonOrThrow(res);
      return ExternalProofSalesRoomClientSafeViewV1Schema.parse(body);
    },
    async approveExternalProofSalesRoom(
      clientLinkId: string,
      token: string,
      salesRoomId: string,
      viewVersion: string,
      approvedByName?: string,
      note?: string,
    ) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/approval`,
        {
          method: "POST",
          headers: authHeaders(token, { "content-type": "application/json" }),
          body: JSON.stringify({
            schemaId: "externalProofSalesRoom.approvalSubmission.v1",
            salesRoomId,
            viewVersion,
            approvedByName,
            note,
          }),
        },
      );
      const body = await parseJsonOrThrow(res);
      return ExternalProofSalesRoomApprovalRecordV1Schema.parse(body);
    },
    async getExternalAfterFixProof(clientLinkId: string, token: string) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/external-after-fix-proof`,
        { method: "GET", headers: authHeaders(token) },
      );
      const body = await parseJsonOrThrow(res);
      return ExternalAfterFixProofClientSafeSummaryV1Schema.parse(body);
    },
    async getProjectRecordCenter(clientLinkId: string, token: string) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/record-center`,
        { method: "GET", headers: authHeaders(token) },
      );
      const body = await parseJsonOrThrow(res);
      return SanitizedProjectRecordCenterClientViewV1Schema.parse(body);
    },
    async listClarifications(clientLinkId: string, token: string) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/clarifications`,
        { method: "GET", headers: authHeaders(token) },
      );
      const body = await parseJsonOrThrow(res);
      return ClarificationListV1Schema.parse(body);
    },
    async respondToClarification(
      clientLinkId: string,
      token: string,
      clarificationRequestId: string,
      body: ClarificationRespondRequestV1,
    ) {
      const parsedBody = ClarificationRespondRequestV1Schema.parse(body);
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/clarifications/${enc(clarificationRequestId)}/respond`,
        {
          method: "POST",
          headers: authHeaders(token, { "content-type": "application/json" }),
          body: JSON.stringify(parsedBody),
        },
      );
      const json = await parseJsonOrThrow(res);
      return ClarificationActionResultV1Schema.parse(json);
    },
    async getJourneyPresentation(clientLinkId: string, token: string) {
      const res = await fetchImpl(
        `${config.apiBaseUrl}/v1/client-links/${enc(clientLinkId)}/journey-presentation`,
        { method: "GET", headers: authHeaders(token) },
      );
      const body = await parseJsonOrThrow(res);
      return ClientJourneyPresentationV1Schema.parse(body);
    },
  };
}
